import React from "react";
import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  Brush,
} from "recharts";

import RxGhgAccordion from "./RxGhgAccordion";

export default function PickupsChartPanel({
  pickupsTrendInReportRange,
  pickupsMetric,
  setPickupsMetric,
  emissionsPeriod,
  setEmissionsPeriod,
  isExporting,
  pickupsChartRef,
}) {
  const { t } = useTranslation();

  const pointsCount = Array.isArray(pickupsTrendInReportRange)
    ? pickupsTrendInReportRange.length
    : 0;

  const periodLabel = (p) =>
    p === "day"
      ? t("ghg.period.day", "Daily")
      : p === "week"
      ? t("ghg.period.week", "Weekly")
      : t("ghg.period.month", "Monthly");

  const metricLabel =
    pickupsMetric === "count"
      ? t("ghg.pickups.metric.count", "Pickups: # of requests")
      : t("ghg.pickups.metric.qty", "Pickups: total quantity");

  return (
    <Box className="rxghg_block">
      <RxGhgAccordion
        id="rxghg_pickups_trend"
        title={t("ghg.pickups.title", "Pickups over time")}
        subtitle={t(
          "ghg.common.deliveredWithinReportRange",
          "Delivered requests within the report date range"
        )}
        rightEl={
          <Box
            className="rxghg_accBadges"
            style={{ display: "flex", gap: 8, alignItems: "center" }}
          >
            <span className="rxghg_badge">
              {t("ghg.common.pointsCount", "{{count}} points", {
                count: pointsCount,
              })}
            </span>

            <FormControl size="small" className="rxghg_inp" sx={{ minWidth: 180 }}>
              <Select
                value={pickupsMetric}
                onChange={(e) => setPickupsMetric(e.target.value)}
                renderValue={() => metricLabel}
              >
                <MenuItem value="count">{t("ghg.pickups.metric.count", "Pickups: # of requests")}</MenuItem>
                <MenuItem value="qty">{t("ghg.pickups.metric.qty", "Pickups: total quantity")}</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" className="rxghg_inp" sx={{ minWidth: 140 }}>
              <Select
                value={emissionsPeriod}
                onChange={(e) => setEmissionsPeriod(e.target.value)}
                renderValue={(v) => periodLabel(v)}
              >
                <MenuItem value="day">{t("ghg.period.day", "Daily")}</MenuItem>
                <MenuItem value="week">{t("ghg.period.week", "Weekly")}</MenuItem>
                <MenuItem value="month">{t("ghg.period.month", "Monthly")}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        }
      >
        <Box sx={{ height: 320, width: "100%" }} ref={pickupsChartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={pickupsTrendInReportRange}
              margin={{ top: 10, right: 20, left: 20, bottom: 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
              <XAxis dataKey="bucket" tickMargin={8} minTickGap={24} />
              <YAxis
                tickMargin={8}
                allowDecimals={pickupsMetric !== "count"}
                label={{
                  value:
                    pickupsMetric === "count"
                      ? t("ghg.pickups.axisY.count", "Pickups (requests)")
                      : t("ghg.pickups.axisY.qty", "Pickups (quantity)"),
                  angle: -90,
                  position: "insideLeft",
                  offset: 0,
                }}
              />

              <RTooltip
                formatter={(v) => {
                  const n = Number(v);
                  if (!Number.isFinite(n))
                    return ["—", t("ghg.pickups.tooltip.pickups", "Pickups")];

                  if (pickupsMetric === "count") {
                    return [
                      n.toFixed(0),
                      t("ghg.pickups.tooltip.requests", "Requests"),
                    ];
                  }

                  return [
                    n.toFixed(2),
                    t("ghg.pickups.tooltip.quantity", "Quantity"),
                  ];
                }}
                labelFormatter={(label) =>
                  t("ghg.common.tooltip.periodLabel", "Period: {{label}}", {
                    label,
                  })
                }
              />

              <Line
                type="monotone"
                dataKey="value"
                stroke="var(--ghg-blue)"
                strokeWidth={2}
                dot={false}
              />

              {!isExporting && <Brush dataKey="bucket" height={24} travellerWidth={10} />}
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="caption" sx={{ opacity: 0.75, mt: 1, display: "block" }}>
          {pickupsMetric === "count"
            ? t(
                "ghg.pickups.caption.count",
                "Each point = number of delivered requests in that period."
              )
            : t(
                "ghg.pickups.caption.qty",
                "Each point = total delivered quantity in that period (weight/quantity from request or listing)."
              )}
        </Typography>
      </RxGhgAccordion>
    </Box>
  );
}